import { Avatar, Box, Chip, Stack, Tooltip } from "@mui/joy";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { SDelete, SEdit, SHourglass, SPlay, SProduct, SStop } from "../../../styles/StyledMUI";
import {
  colorTokens,
  gradientListRowCardShellSx,
  listRowIconButtonPlainSx,
  textTokens,
  withAlpha,
} from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import type { Rental, RentalLifeStatus } from "../../../api/rental/Rental.typed";
import {
  allowedRentalTransitions,
  rentalLifeStatusColor,
  rentalLifeStatusLabel,
  rentalTransitionBlock,
} from "../rentalStatusHelpers";
import { formatPrice, htToTtc } from "../../billing/shared/billingUtils";

type Props = {
  rental: Rental;
  customerLabel: string;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onChangeStatus: (status: RentalLifeStatus) => void | Promise<void>;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDay(value: string): number {
  const d = new Date(value);

  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Inclusive day count: 15 → 17 = 3 days.
function computeDays(start?: string | null, end?: string | null): number {
  if (!start || !end) return 1;
  const s = startOfLocalDay(start);
  const e = startOfLocalDay(end);
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return 1;

  return Math.floor((e - s) / MS_PER_DAY) + 1;
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy", { locale: fr });
  } catch {
    return value;
  }
}

export default function RentalCard({
  rental,
  customerLabel,
  canEdit,
  onEdit,
  onDelete,
  onChangeStatus,
}: Props) {
  const transitions = allowedRentalTransitions(rental.status);
  const blockActive = rentalTransitionBlock(rental, "active");
  const blockReturned = rentalTransitionBlock(rental, "returned");
  const fieldLabelBoxSx = {
    color: textTokens.color.subtle,
    fontWeight: 600,
    mb: 0.3,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    fontSize: "0.65rem",
  };

  const lockedValueBoxSx = {
    color: textTokens.color.primary,
    fontWeight: 400,
    lineHeight: 1.3,
    fontSize: "0.8rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  };

  const detailsGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
      lg: "repeat(auto-fit, minmax(148px, 1fr))",
    },
    gap: 1,
    alignItems: "start",
  };

  const items = rental.items ?? [];
  const activeItems = items.filter((i) => !i.returned_at).length;
  const days = computeDays(rental.started_at, rental.ended_at);
  const isKitMode = rental.rental_product_id != null;
  const dailyHT = isKitMode
    ? rental.price_ht
    : items
        .filter((i) => !i.returned_at && i.unit_price_ht != null)
        .reduce((acc, i) => acc + (i.unit_price_ht ?? 0), 0);
  const dailyTtc = isKitMode
    ? htToTtc(rental.price_ht, rental.tax_rate)
    : items
        .filter((i) => !i.returned_at && i.unit_price_ht != null)
        .reduce((acc, i) => acc + htToTtc(i.unit_price_ht ?? 0, i.tax_rate ?? 0), 0);
  const totalHT = dailyHT * days;
  const totalTTC = dailyTtc * days;
  const displayCurrency = isKitMode
    ? rental.currency
    : (items.find((i) => i.currency)?.currency ?? rental.currency);

  return (
    <Box sx={gradientListRowCardShellSx}>
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                display: { xs: "none", sm: "flex" },
                width: 40,
                height: 40,
                flexShrink: 0,
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: colorTokens.accent.primary,
                border: `1px solid ${colorTokens.border.default}`,
              }}
              aria-hidden
            >
              <SProduct sx={{ fontSize: "1.25rem" }} />
            </Avatar>
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <DarkTypography
                  level="title-md"
                  colorVariant="primary"
                  sx={{
                    fontWeight: 700,
                    minWidth: 0,
                    overflow: "hidden",
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    textOverflow: { sm: "ellipsis" },
                  }}
                >
                  {customerLabel}
                </DarkTypography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={rentalLifeStatusColor(rental.status)}
                  sx={{ flexShrink: 0 }}
                >
                  {rentalLifeStatusLabel(rental.status)}
                </Chip>
              </Box>
              <DarkTypography level="body-xs" colorVariant="muted">
                Location #{rental.id}
                {rental.rental_product?.name ? ` · ${rental.rental_product.name}` : " · à l'unité"}
              </DarkTypography>
            </Stack>
          </Box>

          <Box sx={{ display: "flex", gap: 0.3, flexShrink: 0, alignItems: "center", ml: "auto" }}>
            {canEdit && transitions.includes("draft") && (
              <Tooltip title="Mettre en brouillon">
                <IconButtonCTA
                  ctaVariant="neutral"
                  size="sm"
                  variant="plain"
                  onClick={() => onChangeStatus("draft")}
                  aria-label="Mettre la location en brouillon"
                  sx={listRowIconButtonPlainSx}
                >
                  <SHourglass />
                </IconButtonCTA>
              </Tooltip>
            )}
            {canEdit && transitions.includes("active") && (
              <Tooltip title={blockActive ?? "Démarrer la location"}>
                <span>
                  <IconButtonCTA
                    ctaVariant="success"
                    size="sm"
                    variant="plain"
                    disabled={blockActive != null}
                    onClick={() => onChangeStatus("active")}
                    aria-label="Démarrer la location"
                    sx={{
                      ...listRowIconButtonPlainSx,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
                    }}
                  >
                    <SPlay />
                  </IconButtonCTA>
                </span>
              </Tooltip>
            )}
            {canEdit && transitions.includes("returned") && (
              <Tooltip title={blockReturned ?? "Clore — passer en retour"}>
                <span>
                  <IconButtonCTA
                    ctaVariant="danger"
                    size="sm"
                    variant="plain"
                    disabled={blockReturned != null}
                    onClick={() => onChangeStatus("returned")}
                    aria-label="Clore la location"
                    sx={{
                      ...listRowIconButtonPlainSx,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                    }}
                  >
                    <SStop />
                  </IconButtonCTA>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Modifier">
              <IconButtonCTA
                ctaVariant="accent"
                size="sm"
                variant="plain"
                onClick={onEdit}
                aria-label="Modifier la location"
                sx={{
                  ...listRowIconButtonPlainSx,
                  "&:hover": { backgroundColor: withAlpha(colorTokens.accent.primary, 0.1) },
                }}
              >
                <SEdit />
              </IconButtonCTA>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Supprimer la location">
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  variant="plain"
                  onClick={onDelete}
                  aria-label="Supprimer la location"
                  sx={{
                    ...listRowIconButtonPlainSx,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                  }}
                >
                  <SDelete />
                </IconButtonCTA>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={detailsGridSx}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Items
              </DarkTypography>
            </Box>
            <Box sx={lockedValueBoxSx}>
              {items.length} (actifs : {activeItems})
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Tarif journalier
              </DarkTypography>
            </Box>
            <Box sx={lockedValueBoxSx}>
              {formatPrice(dailyHT, displayCurrency)} HT · {formatPrice(dailyTtc, displayCurrency)}{" "}
              TTC
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Période
              </DarkTypography>
            </Box>
            <Box sx={lockedValueBoxSx}>
              {formatDate(rental.started_at)} → {formatDate(rental.ended_at)}
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Total ({days} j)
              </DarkTypography>
            </Box>
            <Box sx={lockedValueBoxSx}>
              {formatPrice(totalHT, displayCurrency)} HT · {formatPrice(totalTTC, displayCurrency)}{" "}
              TTC
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
